import { FC, useContext } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { TributeCardWizardContext } from '@modules/Tributes/contexts/TributeCardWizardContext';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';


type Props = {
  onSubmit: () => void;
  slots?: {
    submitButton?: LoadingButtonProps;
  }
}

export const ConfirmationStep: FC<Props> = ({ slots, onSubmit }) => {
  const dictionary = useDictionary();
  const { onPreviousStep, deliveryDetails, croppedImageBase64 } = useContext(TributeCardWizardContext);

  return (
    <Box>
      <Stack gap={1} alignItems="center" mt={5} mb={4}>
        <Typography variant="h3" fontSize={18} fontWeight={600}>{dictionary.tributes.edit.card.cardDialog.confirmation}</Typography>
        <Typography variant="h6" color="text.secondary" fontSize={16}>
          {dictionary.tributes.edit.card.cardDialog.confirmationSubtitle}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          sx={{
            position: 'relative',
            height: 250,
          }}
        >

          {croppedImageBase64 && (
            <img
              src={croppedImageBase64}
              height="100%"
              alt="Tribute Card Preview"
              style={{
                border: '3px solid #d1d1d4',
                borderRadius: '12px',
              }}
            />
          )}
          <Box
            sx={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              background: '#ffffffe6',
              py: 0.5,
              px: 1,
              borderRadius: '4px',
              opacity: 0.7,
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>LGCY</Typography>
          </Box>
        </Box>
      </Stack>
      <Stack
        gap={2}
        sx={{
          bgcolor: '#f9f9fa',
          borderRadius: '12px',
          p: 2,
          mt: 3,
        }}
      >
        <Typography variant="h3" fontSize={16} fontWeight={500}>
          {dictionary.tributes.edit.card.cardDialog.shippingTo}
        </Typography>

        <Stack>
          <Typography variant="body1" fontSize={14} color="text.secondary">
            {deliveryDetails?.fullName}
          </Typography>
          <Typography variant="body1" fontSize={14} color="text.secondary">
            {deliveryDetails?.addressLine1}
          </Typography>
          <Typography variant="body1" fontSize={14} color="text.secondary">
            {deliveryDetails?.city}, {deliveryDetails?.stateProvince} {deliveryDetails?.zipPostalCode}
          </Typography>
          <Typography variant="body1" fontSize={14} color="text.secondary">
            {deliveryDetails?.country}
          </Typography>
        </Stack>
      </Stack>

      <Alert severity="success" icon={false} sx={{ mt: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} width="100%" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography variant="body2" fontWeight={600}>
            {dictionary.tributes.edit.card.cardDialog.totalPrice}
          </Typography>
          <Typography variant="body2">
            {dictionary.tributes.edit.card.cardDialog.alreadyIncluded}
          </Typography>
        </Stack>
      </Alert>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={4}
      >
        <Button
          variant="outlined"
          color="default"
          onClick={onPreviousStep}
          sx={{
            px: 3,
            py: 1,
          }}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={onSubmit}
          {...slots?.submitButton}
          sx={{
            px: 3,
            py: 1,
          }}
          endIcon={<ArrowForward />}
        >
          Next
        </LoadingButton>
      </Stack>
    </Box>
  );
};
